import React, { useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  Paper,
} from "@mui/material";
import { Button } from "react-bootstrap";

// Generate sample data
const generateData = (startDate, endDate, points) => {
  const data = [];
  const timeRange = endDate.getTime() - startDate.getTime();
  const timeStep = timeRange / (points - 1);
  const baseValue = 5000;

  for (let i = 0; i < points; i++) {
    const currentDate = new Date(startDate.getTime() + timeStep * i);
    let variation = Math.sin(i / 5) * 1500;
    if (i > points / 2) {
      variation += Math.sin(i / 3) * 1000;
    }
    const followers = Math.max(3000, Math.round(baseValue + variation));
    data.push({ date: currentDate, followers });
  }

  return data;
};

const fullData = generateData(
  new Date("2022-04-16"),
  new Date("2024-12-24"),
  100
);

const filterData = (data, months) => {
  if (!months) return data;
  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - months);
  return data.filter((item) => new Date(item.date) >= cutoffDate);
};

const formatDate = (date) => {
  const day = date.getDate();
  const month = date
    .toLocaleString("default", { month: "short" })
    .toUpperCase();
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 1.5,
          backgroundColor: "white",
          border: "1px solid #f0f0f0",
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {formatDate(new Date(label))}
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {payload[0].value.toLocaleString()} followers
        </Typography>
      </Paper>
    );
  }
  return null;
};

export default function FollowersGraph() {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState("all");

  const handleTimeRangeChange = (event, newTimeRange) => {
    if (newTimeRange !== null) {
      setTimeRange(newTimeRange);
    }
  };

  const getFilteredData = () => {
    switch (timeRange) {
      case "3m":
        return filterData(fullData, 3);
      case "6m":
        return filterData(fullData, 6);
      case "1y":
        return filterData(fullData, 12);
      default:
        return fullData;
    }
  };

  const filteredData = getFilteredData();
  const maxFollowers = Math.max(...filteredData.map((item) => item.followers));
  const roundedMax = Math.ceil(maxFollowers / 1000) * 1000;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid rgba(238, 238, 238, 1)",
        maxWidth: "100%",
        marginTop: "10px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography className="heading" fontWeight="bold">
          Followers Growth
        </Typography>

        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleTimeRangeChange}
          aria-label="time range"
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              borderRadius: 4,
              mx: 0.5, // space between buttons
              px: 2,
              py: 0.5,
              color: "text.secondary",
              backgroundColor: " rgba(247, 242, 250, 1)",
              border: "1px solid transparent", // remove default border
              "&.Mui-selected": {
                backgroundColor: "transparent", // no bg color
                color: "text.primary",
                fontWeight: "medium",
                boxShadow: " 0px 1px 3px 1px rgba(0, 0, 0, 0.15)",
              },
              "&:hover": {
                backgroundColor: " rgba(247, 242, 250, 1)",
              },
            },
          }}
        >
          <ToggleButton value="all" aria-label="all time">
            All
          </ToggleButton>
          <ToggleButton value="1y" aria-label="1 year">
            1Y
          </ToggleButton>
          <ToggleButton value="6m" aria-label="6 months">
            6M
          </ToggleButton>
          <ToggleButton value="3m" aria-label="3 months">
            3M
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ height: 400, width: "100%", mt: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={filteredData}
            margin={{
              top: 20,
              right: 20,
              left: 0,
              bottom: 20,
            }}
          >
            <defs>
              <linearGradient id="colorFollowers" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(107, 142, 118, 0.3)" />
                <stop offset="100%" stopColor="rgba(107, 142, 118, 0.05)" />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => {
                const d = new Date(date);
                return `${d.getDate()} ${d
                  .toLocaleString("default", {
                    month: "short",
                  })
                  .toUpperCase()}, ${d.getFullYear()}`;
              }}
              tick={{ fontSize: 12 }}
              tickCount={5}
              stroke="#ccc"
            />
            <YAxis
              domain={[0, roundedMax]}
              tickFormatter={(value) => `${value / 1000}k`}
              tick={{ fontSize: 12 }}
              stroke="#ccc"
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="followers"
              stroke="rgba(107, 142, 118, 1)"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorFollowers)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
